// Which Payment statuses count as "money has actually moved".
//
// MUST stay in step with the API's PAID_STATUS (StatsController) and
// PaymentsController.outstandingLines, or a counterparty row can never
// reconcile with its own drill-down.
//
// This lived as four separate copies — MonthEnd/index.jsx,
// MonthEnd/Components/CounterpartyLedgerModal.jsx,
// Payments/Components/PaymentAllocationRows.jsx and
// the Order Ledger. Two of them had
// 'refunded' and two did not, so the Order Ledger and the allocation
// picker disagreed with Month End about the same booking. One copy is the
// only way to keep them honest.
//
// WHY 'refunded' IS IN THE SET
//
// A refund is stored as a PAIR. `PaymentService.refundPayment` writes an
// opposite-direction offset row that stays status='completed', and on a
// FULL refund it also flips the original to status='refunded'. (On a
// PARTIAL refund the original stays 'completed' and only the offset is
// written.) Both legs are real movements.
//
// Every consumer below signs its contribution by direction, so the pair
// nets to zero on its own. Dropping the 'refunded' original while still
// counting its offset subtracts the refund TWICE: the per-line paid total
// goes phantom-negative and the outstanding balance is overstated by
// exactly the refunded amount. That is what made PU_WEB_3600513 read
// "€200 still due" on a €100 refund, and what made the allocation picker
// pre-fill €200 against a line owing €100. All 42 refunded payments in
// prod are paired, so there is no unpaired-original case to protect.
//
// WHEN NOT TO USE THIS
//
// Only for sums that are SIGNED BY DIRECTION (inbound adds, outbound
// subtracts). A one-directional gross total must NOT use it: with no
// offsetting outbound leg to cancel against, including the refunded
// original would add back money that was given away. Partners'
// PartnerPaymentsModal deliberately keeps its own {completed, reconciled}
// for exactly that reason — it sums direction='in' only.
export const SETTLED_PAYMENT_STATUSES = new Set(['completed', 'reconciled', 'refunded']);

// Convenience predicate for the common `payments.filter(...)` shape.
export function isSettledPayment(payment) {
  return !!payment && SETTLED_PAYMENT_STATUSES.has(payment.status);
}

// The originals that already have a refund offset (full or partial) in a
// loaded list. Their Refund action is withdrawn, so a second partial never
// has to be reckoned against the first.
export function refundedOriginalIds(payments = []) {
  return new Set(
    payments
      .filter((p) => p?.direction === 'out' && p?.parent_payment_id)
      .map((p) => p.parent_payment_id),
  );
}

// Inbound, completed, and not already refunded (`refundedIds` from above).
export function isRefundable(payment, refundedIds) {
  return payment?.direction === 'in'
    && payment?.status === 'completed'
    // eslint-disable-next-line no-underscore-dangle
    && !refundedIds.has(payment?._id);
}

export default SETTLED_PAYMENT_STATUSES;
